import classNames from "classnames";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet } from "react-router-dom";
import { useUser } from "contexts/UserContext";
import { routes } from "pages/routes";
import styles from "./index.module.scss";
import { dynamicKey } from "services/i18n";

/**
 * Layout shell for the admin console: persistent left sidebar + content outlet.
 * Applies the .admin-v2 class that scopes the admin-only stylesheets. Mounted
 * as a pathless layout route in AdminRoutes (full nav) and ContentAdminRoutes
 * (content-scoped nav via the groups prop); the student-facing catalog
 * surfaces (/library, /course) stay outside it.
 */
export default function AdminShell({ groups = NAV_GROUPS }: AdminShellProps) {
  const { t } = useTranslation();
  const user = useUser(false);

  return (
    <div className={classNames("admin-v2", styles.shell)}>
      <aside className={styles.side}>
        <div className={styles.brand}>
          <span className={styles.mark}>t</span>
          <span className={styles.word}>talaera</span>
        </div>

        <nav className={styles.nav}>
          {groups.map(group => (
            <Fragment key={group.label}>
              <span className={styles.groupLabel}>
                {t(dynamicKey(`Admin.${group.label}`))}
              </span>
              {group.items.map(item => (
                <NavLink key={item.to} to={item.to} className={navLinkClassName}>
                  <span className={styles.glyph} aria-hidden>
                    {item.glyph}
                  </span>
                  {t(dynamicKey(`Admin.${item.label}`))}
                </NavLink>
              ))}
            </Fragment>
          ))}
        </nav>

        {user && (
          <div className={styles.foot}>
            <span className={styles.avatar}>{userInitials(user.profile)}</span>
            <span className={styles.footName}>
              {user.profile.first_name} {user.profile.last_name ?? ""}
            </span>
          </div>
        )}
      </aside>

      <div className={styles.content}>
        <Outlet />
      </div>
    </div>
  );
}

export type AdminNavGroup = {
  label: string;
  items: { label: string; to: string; glyph: string }[];
};

type AdminShellProps = {
  /** Sidebar nav groups; defaults to the full admin console nav. Labels are
   * translation keys under the AdminDashboard namespace. */
  groups?: AdminNavGroup[];
};

// Capacity and Engagement are intentionally absent: they are unused and hidden
// from navigation (their routes stay reachable by URL).
const NAV_GROUPS: AdminNavGroup[] = [
  {
    label: "Admin",
    items: [
      { label: "Users", to: routes.admin.userList.path(), glyph: "◧" },
      { label: "Companies", to: routes.admin.companyList.path(), glyph: "▣" },
      { label: "Matching", to: routes.admin.matching.path(), glyph: "◇" },
      { label: "Sessions", to: routes.admin.sessionList.path(), glyph: "◷" },
      { label: "Monitoring", to: routes.admin.monitoringShift.path(), glyph: "◉" },
      { label: "Calendar", to: routes.admin.calendar.path(), glyph: "◫" },
    ],
  },
  {
    label: "Content",
    items: [
      { label: "Library", to: routes.admin.contentFolder.path(), glyph: "▦" },
      { label: "Exercises", to: routes.admin.exerciseList.path(), glyph: "✎" },
      { label: "Tags", to: routes.admin.tagList.path(), glyph: "#" },
      { label: "Adaptive testing", to: routes.admin.adaptiveTesting.path(), glyph: "▤" },
    ],
  },
  {
    label: "Commercial",
    items: [
      { label: "Pricing", to: routes.admin.pricingModelList.path(), glyph: "◈" },
      { label: "Workshops", to: routes.admin.workshopList.path(), glyph: "◎" },
    ],
  },
];

function navLinkClassName({ isActive }: { isActive: boolean }) {
  return classNames(styles.navLink, isActive && styles.active);
}

function userInitials(profile: { first_name: string; last_name: string | null }) {
  return `${profile.first_name.charAt(0)}${profile.last_name?.charAt(0) ?? ""}`.toUpperCase();
}
